import { useEffect, useRef, useState } from "react";
import { Field } from "formik";
import { url } from "../../config";
import axios from "axios";
import closeIcon from "../../assets/icons/close-icon.svg";
import { formatUnderscoreToSpace } from "../../utils/text-format";

export const FormDropDownDynamic = ({
  name = "select",
  label = "Select Field",
  required,
  placeholder = "Search...",
  value,
  reference,
  ...props
}) => {
  const [options, setOptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);
  const listRef = useRef(null);

  const fetchOptions = async (query = "", currentPage = 1, reset = false) => {
    if (loading) return;
    setLoading(true);
    try {
      const response = await axios.post(
        `${url}/model/${reference.tableName}/list`,
        { name: query, page: currentPage }
      );

      const rows = response.data.data?.rows || [];
      const pageFromAPI = response.data.data?.page || currentPage;
      const totalPages = response.data.data?.count || 1;

      const formatted = rows.map((row) => ({
        label: `${row[reference.displayKey]} : ${row[reference.primaryKey]}`,
        value: row[reference.primaryKey],
      }));

      setOptions((prev) => (reset ? formatted : [...prev, ...formatted]));

      setHasMore(pageFromAPI < totalPages);
    } catch (err) {
      console.error("API Error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOptions("", page, true);
  }, []);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setPage(1);
      setHasMore(true);
      fetchOptions(searchTerm, 1, true);
    }, 300);
    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleScroll = (e) => {
    const target = e.target;
    const bottomReached =
      target.scrollHeight - target.scrollTop - target.clientHeight < 15;

    console.log(bottomReached, "bottomReached");

    if (bottomReached && hasMore && !loading) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchOptions(searchTerm, nextPage);
    }
  };

  return (
    <Field name={name}>
      {({ field, form, meta }) => (
        <div className="w-full" ref={containerRef}>
          <label
            className={`text-fs_16 capitalize ${
              meta.touched && meta.error ? "text-errorColor" : "text-darkText"
            }`}
          >
            {formatUnderscoreToSpace(label)}
          </label>
          <div
            className={`border-[1px] rounded-lg mt-1 w-full relative ${
              meta.touched && meta.error
                ? "border-errorColor"
                : "border-greyText"
            }`}
          >
            <input
              type="text"
              placeholder={placeholder}
              className={`px-3 py-2 w-full rounded-lg lg:text-fs_16
                        text-fs_14 text-darkText placeholder:text-greyText placeholder:text-fs_14 md:placeholder:text-fs_16 focus:outline-none`}
              value={options.find((opt) => opt.value === field.value)?.label}
              onFocus={() => setOpen(true)}
              onChange={(e) => {
                form.setFieldValue(name, "");
              }}
            />

            {open && (
              <div
                onScroll={handleScroll}
                className="absolute z-10 bg-white border border-gray-300 w-full max-h-40 overflow-auto shadow-md rounded mt-1 px-1"
              >
                <input
                  type="text"
                  placeholder={"search"}
                  value={searchTerm}
                  onFocus={() => setOpen(true)}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                  }}
                  className="sticky top-0 px-3 py-2 w-full rounded-lg lg:text-fs_16
                        text-fs_14 text-darkText placeholder:text-greyText placeholder:text-fs_14 md:placeholder:text-fs_16 focus:outline-none border-[1px]"
                />
                <ul ref={listRef}>
                  {options.length > 0 ? (
                    options.map((option) => (
                      <li
                        key={option.value}
                        className={`px-3 py-2 hover:bg-gray-100 hover:text-black cursor-pointer  ${
                          form.values?.[field.name] === option.value
                            ? "bg-secondary text-white"
                            : ""
                        }`}
                        onClick={() => {
                          form.setFieldValue(name, option.value);
                          setSearchTerm("");
                          setOpen(false);
                        }}
                      >
                        {option.label}
                      </li>
                    ))
                  ) : (
                    <li className="px-3 py-2 text-gray-500">
                      No results found
                    </li>
                  )}
                  {loading && (
                    <li className="px-3 py-2 text-gray-400 text-center">
                      Loading...
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
          <label className="text-errorColor text-fs_14 ps-1 mt-1 h-[1rem] flex items-center">
            {meta.touched && meta.error && meta.error}
          </label>
        </div>
      )}
    </Field>
  );
};

export const FormMultiDropDownDynamic = ({
  name = "select",
  label = "Select Field",
  required,
  placeholder = "Search...",
  reference,
  ...props
}) => {
  const [options, setOptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [selectedValues, setSelectedValues] = useState([]);
  const containerRef = useRef(null);
  const listRef = useRef(null);

  const fetchOptions = async (query = "", currentPage = 1, reset = false) => {
    if (loading) return;
    setLoading(true);
    try {
      const response = await axios.post(
        `${url}/model/${reference.tableName}/list`,
        { name: query, page: currentPage }
      );

      const rows = response.data.data?.rows || [];
      const pageFromAPI = response.data.data?.page || currentPage;
      const totalPages = response.data.data?.count || 1;

      const formatted = rows.map((row) => ({
        label: `${row[reference.displayKey]} : ${row[reference.primaryKey]}`,
        value: row[reference.primaryKey],
      }));

      setOptions((prev) => (reset ? formatted : [...prev, ...formatted]));
      setHasMore(pageFromAPI < totalPages);
    } catch (err) {
      console.error("API Error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOptions("", page, true);
  }, []);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setPage(1);
      setHasMore(true);
      fetchOptions(searchTerm, 1, true);
    }, 300);
    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleScroll = (e) => {
    const target = e.target;
    const bottomReached =
      target.scrollHeight - target.scrollTop - target.clientHeight < 15;

    if (bottomReached && hasMore && !loading) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchOptions(searchTerm, nextPage);
    }
  };

  return (
    <Field name={name}>
      {({ field, form, meta }) => {
        const toggleSelection = (label, val) => {
          var alreadySelected = selectedValues.find((obj) => obj.value == val);

          console.log("alreadySelected", alreadySelected);

          var newValuesforForm = [];
          var newValuesforVar = [];

          if (alreadySelected) {
            var filteredValues = selectedValues.filter(
              (obj) => obj.value != val
            );
            for (let index = 0; index < filteredValues.length; index++) {
              const element = filteredValues[index];
              newValuesforForm.push(element.value);
            }
            newValuesforVar = [...filteredValues];
          } else {
            for (let index = 0; index < selectedValues.length; index++) {
              const element = selectedValues[index];
              newValuesforForm.push(element.value);
            }
            newValuesforForm.push(val);

            newValuesforVar = [...selectedValues, { label: label, value: val }];
          }
          form.setFieldValue(name, newValuesforForm);

          setSelectedValues([...newValuesforVar]);
        };

        return (
          <div className="w-full" ref={containerRef}>
            <label
              className={`text-fs_16 capitalize ${
                meta.touched && meta.error ? "text-errorColor" : "text-darkText"
              }`}
            >
              {formatUnderscoreToSpace(label)}
            </label>
            <div
              className={`border-[1px] rounded-lg mt-1 w-full relative ${
                meta.touched && meta.error
                  ? "border-errorColor"
                  : "border-greyText"
              }`}
              onClick={() => setOpen(true)}
            >
              <div
                className="flex flex-wrap gap-1 px-2 py-1 min-h-[2rem] items-center bg-white rounded-lg px-3 py-2 w-full
                 rounded-lg lg:text-fs_16 text-fs_14 text-darkText"
              >
                {selectedValues.length === 0 ? (
                  <span className="text-gray-400 text-sm">{placeholder}</span>
                ) : (
                  <>
                    {selectedValues.map((opt) => (
                      <div
                        key={opt.value}
                        className="flex bg-gray-100 rounded-md"
                      >
                        <p className="text-sm ps-2 py-1 max-w-[150px] truncate">
                          {opt.label}
                        </p>
                        <button
                          className="p-1 px-2 text-fs_14 hover:bg-gray-300 rounded-md"
                          onClick={() => toggleSelection(opt.label, opt.value)}
                        >
                          <img src={closeIcon} width={16} />
                        </button>
                      </div>
                    ))}
                  </>
                )}
              </div>

              {open && (
                <div
                  onScroll={handleScroll}
                  className="absolute z-10 bg-white border border-gray-300 w-full max-h-40 overflow-auto shadow-md rounded mt-1 px-1"
                >
                  <input
                    type="text"
                    placeholder={"Search"}
                    value={searchTerm}
                    onFocus={() => setOpen(true)}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="sticky top-0 px-3 py-2 w-full rounded-lg border text-sm focus:outline-none"
                  />
                  <ul ref={listRef}>
                    {options.length > 0 ? (
                      options.map((option) => (
                        <li
                          key={option.value}
                          className={`px-3 py-2 cursor-pointer hover:bg-gray-100 hover:text-black ${
                            selectedValues.find(
                              (obj) => obj.value == option.value
                            )
                              ? "bg-secondary text-white"
                              : ""
                          }`}
                          onClick={() =>
                            toggleSelection(option.label, option.value)
                          }
                        >
                          {option.label}
                        </li>
                      ))
                    ) : (
                      <li className="px-3 py-2 text-gray-500">
                        No results found
                      </li>
                    )}
                    {loading && (
                      <li className="px-3 py-2 text-gray-400 text-center">
                        Loading...
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
            <label className="text-errorColor text-fs_14 ps-1 mt-1 h-[1rem] flex items-center">
              {meta.touched && meta.error && meta.error}
            </label>
          </div>
        );
      }}
    </Field>
  );
};
